<script lang="ts">
	import { number, t } from 'svelte-i18n';
	import BarChartStackedVerticalTime from '$lib/components/charts/BarChartStackedVerticalTime.svelte';
	import ButtonTextPrimary from '$lib/elements/buttons/variants/ButtonTextPrimary.svelte';
	import type { InputRangeProps } from '$lib/elements/inputs/types';
	import ProgressIndicator from '$lib/elements/ProgressIndicator.svelte';
	import { isDateAfter, parseApiDate } from '$lib/utils/date';
	import { capitalize, formatAmountWithCurrency } from '$lib/utils/strings';
	import type { BondsSimulator } from '@app/models/api/BondsSimulator';
	import {
		getOperationsTypeInterestStringValue,
		isObligationsTypeInteretsInFine,
		OperationsTypeInteretsEnum
	} from '@app/models/api/enums/ObligationsTypeInteretsEnum';
	import type { OperationModel } from '@app/models/api/OperationModel';
	import { getOperationSimulation } from '@app/services/api/operations';
	import { debounceAsync, minimumFeesFreeInvestment } from '@app/utils/global';
	import { getEntryFees } from '@app/utils/operations';
	import SliderInvest from '../../../inputs/SliderInvest.svelte';
	import RealEstateSimulatorTimeline from './RealEstateSimulatorTable.svelte';
	import { urls } from '@app/urls.js';
	import { browser } from '$app/environment';

	export let operation: OperationModel;

	let sliderIntentionProps: InputRangeProps = {
		value: operation.minCoupon,
		hideTooltip: true,
		min: operation.minCoupon,
		max: operation.maxCoupon,
		step: operation.subscriptionLevel,
		thumbColor: 'primary',
		thumbWidth: 25,
		trackColor: 'primary-light',
		trackHeight: 2,
		required: true,
		validation: {
			rules: [
				{
					label: $t('slideInvest.betweenValues', {
						values: { min: operation.minCoupon, max: operation.maxCoupon }
					}),
					check: (v: number) => v >= operation.minCoupon && v <= operation.maxCoupon
				}
			]
		}
	};

	let nbMonths: number;
	let rate: number;
	let nbTimeline: number;
	let entryFees: number;
	let earned: number;
	let error = '';
	let isLoading = false;
	let chartProps;
	let bondsSimulator: BondsSimulator;
	let isChartActive = true;
	let data;

	const getData = async () => {
		error = '';
		isLoading = true;
		try {
			data = await getOperationSimulation(
				operation.id.toString(),
				sliderIntentionProps.value as number,
				`${window.location.protocol}//${window.location.host}`
			);
			bondsSimulator = data;
			nbMonths = data.dureeEnMois;
			rate = data.tauxRendement;
			nbTimeline = data.echeancier.length;
			entryFees =
				(sliderIntentionProps.value && sliderIntentionProps.value < minimumFeesFreeInvestment) ||
				sliderIntentionProps.value >= 100000
					? (getEntryFees(operation) * data.montant) / 100
					: 0;
			earned = data.rendementATermeReel;
			let startDate = parseApiDate(data.dateEmissionEmpruntOblig);
			if (isObligationsTypeInteretsInFine(operation.obligationsTypeRemboursementInterets)) {
				isChartActive = false;
			} else {
				chartProps = data.echeancier
					.map((e, i) => {
						startDate.setMonth(startDate.getMonth() + e.dureeEnMois);
						if (isDateAfter(startDate, data.dateFinEmpruntOblig)) {
							startDate = parseApiDate(data.dateFinEmpruntOblig);
						}
						let array = [
							{
								group: capitalize($t('operation.simulator.chart.1')),
								date: startDate.toString(),
								value: e.capitalRembourseEnFinDePeriode
							},
							{
								group: capitalize($t('operation.simulator.chart.2')),
								date: startDate.toString(),
								value: e.interetsEnFinDePeriode
							}
						];
						if (e.primeNonConversion) {
							array.push({
								group: capitalize($t('operation.simulator.chart.3')),
								date: startDate.toString(),
								value: e.primeNonConversion
							});
						}
						return array;
					})
					.flat();
			}
		} catch (e) {
			error = $t('errors.unhandled');
			data = null;
			throw e;
		} finally {
			isLoading = false;
		}
	};

	const debounce = debounceAsync(async () => {
		await getData();
	}, 500);

	$: if (
		sliderIntentionProps.value &&
		operation.obligationsTypeRemboursementInterets !==
			(OperationsTypeInteretsEnum.INTERETS_TRIMESTRIELS_SUR_CAPITAL_AMORTI &&
				OperationsTypeInteretsEnum.INTERETS_MENSUELS_SUR_CAPITAL_AMORTI) &&
		browser
	) {
		debounce();
	}
</script>

{#if data}
	<section>
		<h2>{capitalize($t('operation.simulator.title'))}</h2>
		<div class="space-y-4">
			<SliderInvest bind:props={sliderIntentionProps} />
			<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
				<div class="flex flex-col justify-between">
					<div class="border-b border-grey-light py-2 space-y-1">
						<div>{capitalize($t('operation.simulator.capital'))}</div>
						<div class="font-semibold">
							{capitalize($t('operation.simulator.duration', { values: { month: nbMonths } }))}<sup
								>*</sup
							>
						</div>
					</div>

					<div class=" py-2 space-y-1">
						<div>
							{#if isObligationsTypeInteretsInFine(operation.obligationsTypeRemboursementInterets)}
								{getOperationsTypeInterestStringValue(
									operation.obligationsTypeRemboursementInterets,
									$t
								)}
							{:else}
								{capitalize($t('operation.simulator.interest'))}
							{/if}
						</div>
						<div class="font-bold">
							{@html capitalize(
								$t('operation.simulator.rate', {
									values: {
										value: $number(rate, {
											style: 'percent',
											maximumSignificantDigits: 3
										})
									}
								})
							)}
						</div>
					</div>
				</div>

				<div class="flex flex-col justify-between">
					<div class="border-b border-grey-light py-2">
						{capitalize($t('operation.simulator.earn'))}
						<span class="font-semibold text-primary">+{formatAmountWithCurrency(earned ?? 0)}</span
						><sup>(1)</sup>
					</div>

					<div class="border-b border-grey-light py-2">
						{#if isObligationsTypeInteretsInFine(operation.obligationsTypeRemboursementInterets)}
							{capitalize($t('operation.simulator.earnDurationInFine'))}
						{:else}
							{@html capitalize(
								$t('operation.simulator.earnDuration', {
									values: { nbTransactions: nbTimeline ?? 0, nbMonths: nbMonths ?? 0 }
								})
							)}
						{/if}
					</div>

					<div class="py-2">
						{@html capitalize(
							$t('operation.simulator.entryFees', {
								values: { value: formatAmountWithCurrency(entryFees) }
							})
						)}
					</div>
				</div>
			</div>

			<div>
				<h3 class="flex items-center justify-between">
					<span>{capitalize($t('operation.simulator.chartTitle'))}</span>
					{#if !isObligationsTypeInteretsInFine(operation.obligationsTypeRemboursementInterets)}
						<div>
							<ButtonTextPrimary
								props={{
									iconLeft: 'analytics',
									classNames: isChartActive ? 'underline' : ''
								}}
								on:click={() => (isChartActive = true)}
							/>
							<ButtonTextPrimary
								props={{ iconLeft: 'list', classNames: isChartActive ? '' : 'underline' }}
								on:click={() => (isChartActive = false)}
							/>
						</div>
					{/if}
				</h3>
				<div>
					{#if isLoading}
						<div class="flex items-center justify-center h-[300px]">
							<ProgressIndicator appearance="circular" />
						</div>
					{:else if isChartActive && chartProps}
						<BarChartStackedVerticalTime data={chartProps} height="300px" />
					{:else if !isChartActive && bondsSimulator}
						<RealEstateSimulatorTimeline
							infos={bondsSimulator}
							typeInteret={operation.obligationsTypeRemboursementInterets}
						/>
					{/if}
				</div>
			</div>

			<div>
				<p>
					{@html $t('operation.simulator.legalInfo')} (<ButtonTextPrimary
						props={{
							label: $t('readMore'),
							href: urls.LEGAL.RISK_WARNING
						}}
					/>).
				</p>
				{#if bondsSimulator?.echeancier.some((e) => e.primeNonConversion > 0)}
					<p>{@html $t('operation.simulator.legalInfoPNC')}</p>
				{/if}
			</div>
		</div>
	</section>
{/if}
